import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.ts";
import { keys } from "../lib/query.ts";

async function unwrap<T>(promise: Promise<{ data: T | null; error: unknown }>): Promise<T> {
  const { data, error } = await promise;
  if (error !== null) throw (error as { value?: unknown }).value ?? error;
  if (data === null) throw new Error("the API returned no body");
  return data;
}

type Franchise = ReturnType<typeof api.v1.franchises>;

export interface HourSpan {
  readonly weekday: number;
  readonly start_time: string;
  readonly end_time: string;
}

export type OpeningHours = NonNullable<
  Awaited<ReturnType<Franchise["opening-hours"]["get"]>>["data"]
>;
export type ResourceList = NonNullable<Awaited<ReturnType<Franchise["resources"]["get"]>>["data"]>;
export type ResourceView = ResourceList["data"][number];
export type ServiceSettings = NonNullable<
  Awaited<ReturnType<Franchise["service-settings"]["get"]>>["data"]
>;
export type ServiceSettingView = ServiceSettings["data"][number];
export type BookingRules = NonNullable<
  Awaited<ReturnType<Franchise["booking-rules"]["get"]>>["data"]
>;
export type MobileSettings = NonNullable<
  Awaited<ReturnType<Franchise["mobile-settings"]["get"]>>["data"]
>;
export type Profile = NonNullable<Awaited<ReturnType<Franchise["profile"]["get"]>>["data"]>;
export type NotificationRules = NonNullable<
  Awaited<ReturnType<Franchise["notification-rules"]["get"]>>["data"]
>;
export type NotificationRuleView = NotificationRules["data"][number];
export type Invitations = NonNullable<Awaited<ReturnType<Franchise["invitations"]["get"]>>["data"]>;
export type StaffMember = NonNullable<
  Awaited<ReturnType<ReturnType<Franchise["staff"]>["get"]>>["data"]
>;
export type Reassignment = NonNullable<
  Awaited<ReturnType<ReturnType<Franchise["resources"]>["reassignment"]["get"]>>["data"]
>;

export function useOpeningHours(franchiseId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: keys.openingHours(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string })["opening-hours"].get({
          query: { from, to },
        }),
      ),
  });
}

export function useSaveOpeningHours(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (hours: readonly HourSpan[]) =>
      unwrap(api.v1.franchises({ franchiseId })["opening-hours"].put({ hours: [...hours] })),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.openingHours(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useToggleHoliday(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { date: string; closed: boolean }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .holidays({ date: input.date })
          .put({ closed: input.closed }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.openingHours(franchiseId) });
    },
  });
}

export function useResources(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.resources(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).resources.get()),
  });
}

export function useCreateResource(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: "staff" | "van" | "bay";
      name: string;
      color: string;
      is_bookable?: boolean;
      inherits_hours?: boolean;
    }) => unwrap(api.v1.franchises({ franchiseId }).resources.post(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useUpdateResource(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      resourceId: string;
      name?: string;
      color?: string;
      is_bookable?: boolean;
      is_active?: boolean;
      inherits_hours?: boolean;
    }) => {
      const { resourceId, ...patch } = input;
      return unwrap(api.v1.franchises({ franchiseId }).resources({ resourceId }).patch(patch));
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useSaveResourceHours(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { resourceId: string; hours: readonly HourSpan[] }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .resources({ resourceId: input.resourceId })
          .hours.put({ hours: [...input.hours] }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useSaveResourceSkills(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { resourceId: string; serviceIds: readonly string[] }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .resources({ resourceId: input.resourceId })
          .skills.put({ service_ids: [...input.serviceIds] }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useReassignment(franchiseId: string, resourceId: string) {
  return useQuery({
    queryKey: keys.reassignment(franchiseId, resourceId),
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId }).resources({ resourceId }).reassignment.get()),
  });
}

export function useDeleteResource(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      resourceId: string;
      assignments: readonly { appointment_id: string; resource_id: string }[];
    }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .resources({ resourceId: input.resourceId })
          .delete({ assignments: [...input.assignments] }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useInvitations(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.invitations(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).invitations.get()),
  });
}

export function useCreateInvitation(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: "franchise_staff" }) =>
      unwrap(api.v1.franchises({ franchiseId }).invitations.post(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(franchiseId) });
    },
  });
}

export function useRevokeInvitation(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(api.v1.franchises({ franchiseId }).invitations({ invitationId }).delete()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(franchiseId) });
    },
  });
}

export function useServiceSettings(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.serviceSettings(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string })["service-settings"].get()),
  });
}

export interface ServiceOverridePatch {
  readonly serviceId: string;
  readonly enabled: boolean;
  readonly duration_min: number | null;
  readonly buffer_before_min: number | null;
  readonly buffer_after_min: number | null;
  readonly price_minor: number | null;
  readonly requires_assessment: boolean | null;
  readonly max_per_day: number | null;
}

export function useSaveServiceSetting(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ServiceOverridePatch) => {
      const { serviceId, ...body } = input;
      return unwrap(
        api.v1.franchises({ franchiseId })["service-settings"]({ serviceId }).put(body),
      );
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.serviceSettings(franchiseId) });
    },
  });
}

export function useBookingRules(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.bookingRules(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string })["booking-rules"].get()),
  });
}

export function useSaveBookingRules(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: BookingRules["rules"]) =>
      unwrap(api.v1.franchises({ franchiseId })["booking-rules"].put(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.bookingRules(franchiseId) });
    },
  });
}

export function useMobileSettings(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.mobileSettings(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string })["mobile-settings"].get()),
  });
}

export interface MobilePatch {
  readonly address_line1: string | null;
  readonly address_line2: string | null;
  readonly postcode: string | null;
  readonly city: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly service_radius_m: number;
  readonly travel_speed_mps: number;
  readonly travel_setup_min: number;
  readonly travel_surcharge_minor_per_km: number;
  readonly postcodes: readonly string[];
}

export function useSaveMobileSettings(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: MobilePatch) =>
      unwrap(
        api.v1.franchises({ franchiseId })["mobile-settings"].put({
          ...input,
          postcodes: [...input.postcodes],
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.mobileSettings(franchiseId) });
    },
  });
}

export function useProfile(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.profile(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () => unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).profile.get()),
  });
}

export interface ProfilePatch {
  readonly display_name: string;
  readonly phone: string | null;
  readonly reply_to_email: string;
  readonly address_line1: string | null;
  readonly address_line2: string | null;
  readonly postcode: string | null;
  readonly city: string;
  readonly description: string | null;
  readonly logo_url: string | null;
  readonly review_url: string | null;
  readonly photos: readonly string[];
}

export function useSaveProfile(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfilePatch) =>
      unwrap(
        api.v1.franchises({ franchiseId }).profile.put({ ...input, photos: [...input.photos] }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.profile(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.me() });
    },
  });
}

export function useNotificationRules(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.notificationRules(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string })["notification-rules"].get()),
  });
}

export interface RulePatch {
  readonly event: string;
  readonly offset_minutes: number;
  readonly channel: string;
  readonly template_key: string;
  readonly service_id: string | null;
  readonly is_active: boolean;
  readonly sort_order: number;
}

export function useCreateNotificationRule(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RulePatch) =>
      unwrap(api.v1.franchises({ franchiseId })["notification-rules"].post(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.notificationRules(franchiseId) });
    },
  });
}

export function useUpdateNotificationRule(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RulePatch & { ruleId: string }) => {
      const { ruleId, ...body } = input;
      return unwrap(api.v1.franchises({ franchiseId })["notification-rules"]({ ruleId }).put(body));
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.notificationRules(franchiseId) });
    },
  });
}

export function useDeleteNotificationRule(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) =>
      unwrap(api.v1.franchises({ franchiseId })["notification-rules"]({ ruleId }).delete()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.notificationRules(franchiseId) });
    },
  });
}

export function useReminderPreview(franchiseId: string | null, template: string, locale: string) {
  return useQuery({
    queryKey: keys.reminderPreview(franchiseId ?? "", template, locale),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          ["notification-rules"].preview.post({
            template_key: template,
            locale,
          }),
      ),
  });
}

export function useSendTestNotification(franchiseId: string) {
  return useMutation({
    mutationFn: (input: { template_key: string; locale: string }) =>
      unwrap(api.v1.franchises({ franchiseId })["notification-rules"].test.post(input)),
  });
}

/* ── Google Calendar & ICS feeds (P11) ─────────────────────────────────────── */

export type Integrations = NonNullable<
  Awaited<ReturnType<Franchise["integrations"]["get"]>>["data"]
>;
export type IntegrationView = Integrations["data"][number];
export type IcsFeeds = NonNullable<Awaited<ReturnType<Franchise["ics-feeds"]["get"]>>["data"]>;

export function useIntegrations(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.integrations(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).integrations.get()),
    // The sync status is the one settings screen that goes stale on its own: the worker
    // pushes in the background, so "senast synkad" moves without anyone clicking.
    refetchInterval: 30_000,
  });
}

/**
 * Start the consent flow.
 *
 * The API answers with a URL rather than a redirect, because a 302 to accounts.google.com
 * from `fetch` is a CORS failure. `window.location` is what actually leaves the app.
 */
export function useConnectGoogle(franchiseId: string) {
  return useMutation({
    mutationFn: (resourceId: string) =>
      unwrap(
        api.v1.franchises({ franchiseId }).integrations.google.authorize.post({
          resource_id: resourceId,
        }),
      ),
  });
}

export function useSyncIntegration(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (integrationId: string) =>
      unwrap(api.v1.franchises({ franchiseId }).integrations({ integrationId }).sync.post()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.integrations(franchiseId) });
    },
  });
}

export function useDisconnectIntegration(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { integrationId: string; removeEvents: boolean }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .integrations({ integrationId: input.integrationId })
          .delete(undefined, {
            query: { remove_events: input.removeEvents ? "true" : "false" },
          }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.integrations(franchiseId) });
    },
  });
}

export function useSetCalendarPrivacy(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (privacyLevel: "full" | "initials" | "busy") =>
      unwrap(
        api.v1.franchises({ franchiseId }).integrations.privacy.put({
          privacy_level: privacyLevel,
        }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.integrations(franchiseId) });
    },
  });
}

export function useIcsFeeds(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.icsFeeds(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string })["ics-feeds"].get()),
  });
}

/** Issue or rotate. The response is the only time the URL exists — the screen must show it. */
export function useIssueIcsFeed(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (resourceId: string | null) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          ["ics-feeds"].post(resourceId === null ? {} : { resource_id: resourceId }),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.icsFeeds(franchiseId) });
    },
  });
}

export function useRevokeIcsFeed(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (feedId: string) =>
      unwrap(api.v1.franchises({ franchiseId })["ics-feeds"]({ feedId }).delete()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.icsFeeds(franchiseId) });
    },
  });
}

export type ReportSubscriptions = NonNullable<
  Awaited<ReturnType<Franchise["report-subscriptions"]["get"]>>["data"]
>;
export type ReportSubscriptionView = ReportSubscriptions["data"][number];
export type FranchiseMetrics = NonNullable<
  Awaited<ReturnType<Franchise["metrics"]["get"]>>["data"]
>;

export interface ReportPatch {
  readonly recipient_user_id: string;
  readonly period: "weekly" | "monthly";
  readonly send_weekday?: number | null;
  readonly send_day_of_month?: number | null;
  readonly send_hour: number;
  readonly metrics: string[];
  readonly include_csv?: boolean;
  readonly is_active?: boolean;
}

export function useReportSubscriptions(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.reportSubscriptions(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string })["report-subscriptions"].get(),
      ),
  });
}

export function useReportDeliveries(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.reportDeliveries(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string })["report-deliveries"].get({
          query: {},
        }),
      ),
  });
}

export function useCreateReportSubscription(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: ReportPatch) =>
      unwrap(api.v1.franchises({ franchiseId })["report-subscriptions"].post(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.reportSubscriptions(franchiseId) });
    },
  });
}

export function useUpdateReportSubscription(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: ReportPatch }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          ["report-subscriptions"]({ id: input.id })
          .patch(input.patch),
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.reportSubscriptions(franchiseId) });
    },
  });
}

export function useDeleteReportSubscription(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.v1.franchises({ franchiseId })["report-subscriptions"]({ id }).delete()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.reportSubscriptions(franchiseId) });
    },
  });
}

export function useSendTestReport(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.v1.franchises({ franchiseId })["report-subscriptions"]({ id }).test.post()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.reportDeliveries(franchiseId) });
    },
  });
}

export function useFranchiseMetrics(
  franchiseId: string | null,
  from: string,
  to: string,
  metric: string,
) {
  return useQuery({
    queryKey: keys.franchiseMetrics(franchiseId ?? "", from, to, metric),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string }).metrics.get({
          query: { from, to, metric },
        }),
      ),
  });
}

export type FranchiseMessaging = NonNullable<
  Awaited<ReturnType<Franchise["messaging"]["get"]>>["data"]
>;

export interface MessagingChoice {
  readonly provider_id: string;
  readonly is_active: boolean;
  readonly sender_name: string | null;
}

export function useFranchiseMessaging(franchiseId: string | null) {
  return useQuery({
    queryKey: keys.messaging(franchiseId ?? ""),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).messaging.get()),
  });
}

export function useSaveFranchiseMessaging(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: MessagingChoice) =>
      unwrap(api.v1.franchises({ franchiseId }).messaging.put(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.messaging(franchiseId) });
    },
  });
}

export function useStaffMember(franchiseId: string, resourceId: string) {
  return useQuery({
    queryKey: keys.staffMember(franchiseId, resourceId),
    queryFn: () => unwrap(api.v1.franchises({ franchiseId }).staff({ resourceId }).get()),
  });
}

export function useUpdateStaffAccount(franchiseId: string, resourceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name?: string;
      email?: string;
      phone?: string | null;
      locale?: string;
    }) => unwrap(api.v1.franchises({ franchiseId }).staff({ resourceId }).account.patch(input)),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.staffMember(franchiseId, resourceId) });
      void client.invalidateQueries({ queryKey: keys.resources(franchiseId) });
    },
  });
}

export function useSendPasswordReset(franchiseId: string, resourceId: string) {
  return useMutation({
    mutationFn: () =>
      unwrap(api.v1.franchises({ franchiseId }).staff({ resourceId })["password-reset"].post()),
  });
}

export function useSignOutEverywhere(franchiseId: string, resourceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.v1
        .franchises({ franchiseId })
        .staff({ resourceId })
        .sessions.delete();
      if (error !== null) throw (error as { value?: unknown }).value ?? error;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.staffMember(franchiseId, resourceId) });
    },
  });
}

export function useResendInvitation(franchiseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      unwrap(api.v1.franchises({ franchiseId }).invitations({ invitationId }).resend.post()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.invitations(franchiseId) });
    },
  });
}

export type StaffActivity = NonNullable<
  Awaited<ReturnType<ReturnType<Franchise["staff"]>["activity"]["get"]>>["data"]
>;

export function useStaffActivity(franchiseId: string, resourceId: string) {
  return useQuery({
    queryKey: keys.staffActivity(franchiseId, resourceId),
    queryFn: () => unwrap(api.v1.franchises({ franchiseId }).staff({ resourceId }).activity.get()),
  });
}

export function useStaffTimeOff(franchiseId: string, resourceId: string, from: string, to: string) {
  return useQuery({
    queryKey: [...keys.blockersAll(franchiseId), "resource", resourceId, from, to],
    queryFn: () =>
      unwrap(api.v1.franchises({ franchiseId }).blockers.get({ query: { from, to, resourceId } })),
  });
}

export function useAddTimeOff(franchiseId: string, resourceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: "vacation" | "sick" | "training";
      date: string;
      until: string;
      start_time?: string;
      end_time?: string;
      reason?: string;
    }) =>
      unwrap(
        api.v1.franchises({ franchiseId }).blockers.post({ ...input, resource_id: resourceId }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.blockersAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.staffActivity(franchiseId, resourceId) });
    },
  });
}

export type StaffMetrics = NonNullable<
  Awaited<ReturnType<ReturnType<Franchise["staff"]>["metrics"]["get"]>>["data"]
>;

export function useStaffMetrics(franchiseId: string, resourceId: string, from: string, to: string) {
  return useQuery({
    queryKey: [...keys.staffMember(franchiseId, resourceId), "metrics", from, to],
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .staff({ resourceId })
          .metrics.get({ query: { from, to } }),
      ),
  });
}
